import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GraphQLClient } from "graphql-request";

import { graphqlEndpoint } from "../config";
import { READ_TENANT_USERS } from "../graph/tenantUsers/queries";
import { READ_ROLE_ALLOCATION_BOARD } from "../graph/roleAllocation/queries";
import {
  ASSIGN_TENANT_USER_TO_ROLE,
  MOVE_TENANT_USER_ROLE_ASSIGNMENT,
  SET_PRIMARY_TENANT_USER_ROLE_ASSIGNMENT,
  REMOVE_TENANT_USER_ROLE_ASSIGNMENT,
  ASSIGN_SOP_TO_ROLE,
  MOVE_SOP_ROLE_ASSIGNMENT,
  REMOVE_SOP_ROLE_ASSIGNMENT,
} from "../graph/roleAllocation/mutations";
import { GET_SOP_PROJECTS } from "../graph/projects/queries";

const client = new GraphQLClient(
  graphqlEndpoint
);

export const ROLE_ALLOCATION_VIEW_MODES = {
  PEOPLE: "PEOPLE",
  SOPS: "SOPS",
  ALL: "ALL",
};

const getTenantUserDisplayName = (
  tenantUser
) => {
  if (!tenantUser) {
    return "Unknown user";
  }

  const firstName =
    tenantUser.user?.firstName?.trim() ||
    "";

  const lastName =
    tenantUser.user?.lastName?.trim() ||
    "";

  const fullName =
    `${firstName} ${lastName}`.trim();

  return (
    tenantUser.displayName?.trim() ||
    fullName ||
    tenantUser.user?.email?.trim() ||
    tenantUser.email?.trim() ||
    "Unknown user"
  );
};

const getTenantUserEmail = (
  tenantUser
) => {
  return (
    tenantUser?.user?.email?.trim() ||
    tenantUser?.email?.trim() ||
    ""
  );
};

const getTenantUserHourlyRate = (
  tenantUser
) => {
  const rawHourlyRate =
    tenantUser?.user?.hourlyRate ??
    tenantUser?.hourlyRate ??
    null;

  if (
    rawHourlyRate === null ||
    rawHourlyRate === undefined ||
    rawHourlyRate === ""
  ) {
    return null;
  }

  const hourlyRate = Number(
    rawHourlyRate
  );

  return Number.isFinite(hourlyRate)
    ? hourlyRate
    : null;
};

const normaliseTenantUser = (
  tenantUser
) => {
  if (!tenantUser) {
    return null;
  }

  return {
    ...tenantUser,
    displayName:
      getTenantUserDisplayName(
        tenantUser
      ),
    email:
      getTenantUserEmail(
        tenantUser
      ),
    hourlyRate:
      getTenantUserHourlyRate(
        tenantUser
      ),
  };
};

const enrichRoleAllocationBoard = (
  board,
  tenantUsersById
) => {
  if (!board) {
    return null;
  }

  return {
    ...board,

    roles: (board.roles || []).map(
      (role) => ({
        ...role,

        peopleCards: (
          role.peopleCards || []
        ).map((card) => {
          const cardTenantUser =
            card.tenantUser || null;

          const tenantUserId =
            cardTenantUser?.id ||
            card.assignment
              ?.tenantUserId ||
            card.tenantUserId ||
            null;

          const matchedTenantUser =
            tenantUserId
              ? tenantUsersById.get(
                  tenantUserId
                )
              : null;

          const resolvedTenantUser =
            normaliseTenantUser(
              matchedTenantUser ||
                cardTenantUser
            );

          return {
            ...card,

            tenantUser:
              resolvedTenantUser,

            tenantRole:
              card.tenantRole || role,

            displayName:
              resolvedTenantUser
                ?.displayName ||
              card.displayName ||
              "Unknown user",

            email:
              resolvedTenantUser?.email ||
              "",

            hourlyRate:
              resolvedTenantUser
                ?.hourlyRate ?? null,

            isPrimary:
              card.isPrimary ??
              card.assignment
                ?.isPrimary ??
              false,
          };
        }),
      })
    ),
  };
};

export default function useRoleAllocationBoard({
  tenantId,
  departmentId = null,
  viewMode =
    ROLE_ALLOCATION_VIEW_MODES.PEOPLE,
}) {
  const [board, setBoard] =
    useState(null);

  const [sops, setSops] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [
    tenantUsers,
    setTenantUsers,
  ] = useState([]);

  const fetchBoard =
    useCallback(async () => {
      if (!tenantId) {
        setBoard(null);
        setTenantUsers([]);
        setSops([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const variables = {
          input: {
            tenantId,
            departmentId:
              departmentId || null,
            viewMode,
          },
        };

        const [
          boardResponse,
          usersResponse,
          sopsResponse,
        ] = await Promise.all([
          client.request(
            READ_ROLE_ALLOCATION_BOARD,
            variables
          ),

          client.request(
            READ_TENANT_USERS,
            {
              tenantId,
            }
          ),

          client.request(
            GET_SOP_PROJECTS,
            {
              tenantId,
            }
          ),
        ]);

        const normalisedTenantUsers =
          (
            usersResponse
              .readTenantUsersByTenant ||
            []
          )
            .map(normaliseTenantUser)
            .filter(Boolean);

        const tenantUsersById =
          new Map(
            normalisedTenantUsers.map(
              (tenantUser) => [
                tenantUser.id,
                tenantUser,
              ]
            )
          );

        const enrichedBoard =
          enrichRoleAllocationBoard(
            boardResponse
              .readRoleAllocationBoard,
            tenantUsersById
          );

        setBoard(enrichedBoard);

        setTenantUsers(
          normalisedTenantUsers
        );

        setSops(
          sopsResponse
            .readProjectsFilter || []
        );
      } catch (err) {
        console.error(
          "Failed to load role allocation board:",
          err
        );

        setError(err);
        setBoard(null);
        setTenantUsers([]);
        setSops([]);
      } finally {
        setLoading(false);
      }
    }, [
      tenantId,
      departmentId,
      viewMode,
    ]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const roleColumns = useMemo(
    () => board?.roles || [],
    [board]
  );

  const departments = useMemo(
    () => board?.departments || [],
    [board]
  );

  const assignTenantUserToRole =
    useCallback(
      async ({
        tenantUserId,
        tenantRoleId,
        isPrimary = false,
      }) => {
        return client.request(
          ASSIGN_TENANT_USER_TO_ROLE,
          {
            input: {
              tenantUserId,
              tenantRoleId,
              isPrimary,
            },
          }
        );
      },
      []
    );

  const moveTenantUserRoleAssignment =
    useCallback(
      async ({
        assignmentId,
        tenantRoleId,
      }) => {
        return client.request(
          MOVE_TENANT_USER_ROLE_ASSIGNMENT,
          {
            input: {
              assignmentId,
              tenantRoleId,
            },
          }
        );
      },
      []
    );

  const setPrimaryTenantUserRoleAssignment =
    useCallback(
      async ({ assignmentId }) => {
        return client.request(
          SET_PRIMARY_TENANT_USER_ROLE_ASSIGNMENT,
          {
            input: {
              assignmentId,
            },
          }
        );
      },
      []
    );

  const removeTenantUserRoleAssignment =
    useCallback(
      async ({ assignmentId }) => {
        return client.request(
          REMOVE_TENANT_USER_ROLE_ASSIGNMENT,
          {
            input: {
              assignmentId,
            },
          }
        );
      },
      []
    );

  const assignSOPToRole =
    useCallback(
      async ({
        sopId,
        tenantRoleId,
      }) => {
        return client.request(
          ASSIGN_SOP_TO_ROLE,
          {
            input: {
              sopId,
              tenantRoleId,
            },
          }
        );
      },
      []
    );

  const moveSOPRoleAssignment =
    useCallback(
      async ({
        assignmentId,
        tenantRoleId,
      }) => {
        return client.request(
          MOVE_SOP_ROLE_ASSIGNMENT,
          {
            input: {
              assignmentId,
              tenantRoleId,
            },
          }
        );
      },
      []
    );

  const removeSOPRoleAssignment =
    useCallback(
      async ({ assignmentId }) => {
        return client.request(
          REMOVE_SOP_ROLE_ASSIGNMENT,
          {
            input: {
              assignmentId,
            },
          }
        );
      },
      []
    );

  return {
    board,
    roleColumns,
    departments,
    tenantUsers,
    sops,
    loading,
    error,
    refetch: fetchBoard,

    assignTenantUserToRole,
    moveTenantUserRoleAssignment,
    setPrimaryTenantUserRoleAssignment,
    removeTenantUserRoleAssignment,

    assignSOPToRole,
    moveSOPRoleAssignment,
    removeSOPRoleAssignment,
  };
}